import React, { FC, useCallback } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { makeStyles } from "@material-ui/core";
import Autocomplete from "../../../../components/Autocomplete";
import RequiredDocsMultiSelect from "../components/RequiredDocsMultiSelect";
import AddNewDocuments from "../components/AdditionalDocuments";
import BootstrapInput from "../../../../components/BootstrapInput";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import { IWizardStepContent, WizardMode } from "../../../wizard/types";
import { ViewAcademic } from "../components/ViewAcademic";
import useUserQueries from "../../../user/hooks/useUserQueries";
import { useNotify } from "../../../notify/useNotify";
import { mutateErrors } from "../../../../utils";
import { NotifyMode } from "../../../notify/NotifyModeEnum";
import { IFile } from "../../../../components/UploadFile/types";
import { EducationType } from "../../../school/constants/types";

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
  },
  subContainer: {
    "& + &": {
      marginTop: 16,
    },
  },
  degree: {
    marginBottom: 16,
  },
  degreeNameCard: {
    marginBottom: 16,
  },
}));

const ManageContent: FC<IWizardStepContent> = ({ handleSubmit }) => {
  const classes = useStyles();

  const { values } = useFormState();
  const {
    mutators: { push, remove },
  } = useForm();
  const { uploadApplication } = useUserQueries();
  const snackNotify = useNotify();

  const degrees = values.injectedData.degrees;
  const namePrefix = `degrees.${values.selectedDegree.id}`;
  const selectedDegree = degrees.find(
    (degree) => degree.id === values.selectedDegree.id,
  );
  const selectedDegreeAcademic = selectedDegree.academic;
  const applicationForms: IFile[] =
    values.degrees?.[values.selectedDegree.id]?.applicationForms;

  const addNewFiles = useCallback(
    async (files) => {
      const succeededFileNames: string[] = [];
      const errorMessages: string[] = [];

      for (const file of files) {
        try {
          let { data } = await uploadApplication(file);
          push(`${namePrefix}.applicationForms`, data);
          succeededFileNames.push(file.name);
        } catch (e) {
          const { message } = mutateErrors(e);
          errorMessages.push(`${file.name} - ${message}`);
        }
      }

      if (errorMessages.length > 0) {
        snackNotify("Error", errorMessages.join("\n"), NotifyMode.ERROR);
        return;
      }

      const successMessageEnding =
        succeededFileNames.length > 1
          ? "\nfiles have been uploaded."
          : " has been uploaded.";

      snackNotify(
        "Success",
        succeededFileNames.join("\n") + successMessageEnding,
        NotifyMode.SUCCESS,
      );
    },
    [namePrefix],
  );

  const removeFileHandler = useCallback(
    (file) => {
      const index = applicationForms.indexOf(file);

      remove(`${namePrefix}.applicationForms`, index);
    },
    [applicationForms, namePrefix, remove],
  );

  return (
    <form noValidate onSubmit={handleSubmit}>
      {values.mode === WizardMode.Edit ? (
        <div className={classes.degreeNameCard}>
          <ViewAcademic degreeName={selectedDegree.name} />
        </div>
      ) : (
        <div className={classes.degree}>
          <Field
            disableClearable={true}
            required
            fullWidth
            label="Degree"
            options={degrees}
            placeholder="Degree"
            // infoIcon={'World Education'}
            name={"selectedDegree"}
            component={Autocomplete}
          />
        </div>
      )}

      {degrees.map((degree) => (
        <div key={degree.id}>
          {degree.id === values.selectedDegree.id &&
            (selectedDegreeAcademic && values.mode === WizardMode.Create ? (
              <ViewAcademic
                requiredDocuments={selectedDegreeAcademic.requiredDocs}
                applicationForms={selectedDegreeAcademic.applicationForms}
                applicationLink={selectedDegreeAcademic.applicationLink}
              />
            ) : (
              <div className={classes.container}>
                <div className={classes.subContainer}>
                  <FieldArray
                    name={`${namePrefix}.requiredDocs`}
                    infoText={"Choose minimum 4 required documents"}
                    component={RequiredDocsMultiSelect}
                  />
                </div>
                <div className={classes.subContainer}>
                  <AddNewDocuments
                    namePrefix={namePrefix}
                    educationType={EducationType.Higher}
                  />
                </div>
                <div className={classes.subContainer}>
                  <UploadFile
                    uploadedFiles={applicationForms}
                    addNewFiles={addNewFiles}
                    removeFile={removeFileHandler}
                    infoText={
                      "You can browse hard copy of application form or/and any docs required."
                    }
                    titleText={"Upload Application Form"}
                    allowedFileTypes={["jpg", "jpeg", "png", "pdf"]}
                    maxSize={30}
                    maxFiles={4}
                  />
                </div>
                <div className={classes.subContainer}>
                  <Field
                    fullWidth
                    type="text"
                    label="Application Link"
                    placeholder="www.edu.uk"
                    infoIcon={"You can put the link of application form"}
                    component={BootstrapInput}
                    name={`${namePrefix}.applicationLink`}
                  />
                </div>
              </div>
            ))}
        </div>
      ))}
    </form>
  );
};

export default ManageContent;
